import { useCallback, useMemo } from "react";
import { matchPath, useHistory, useLocation } from "react-router-dom";
import { isValidPackageTab, parseDataStructureType, parseViewFilter } from "./utils";
import type {
  DataStructureTabId,
  PackageTab,
  ViewFilter,
} from "./Settings/components/DataStructure/types";
import type { SupportedTemplateType } from "~/graphql/supergraphTypes";

type UseDataStructureRoutingOptions = {
  dataStructureBasePath: string;
  parentPath: string;
  validTabs: readonly DataStructureTabId[];
  defaultTab?: DataStructureTabId;
  defaultViewFilter?: ViewFilter;
};

const DEFAULT_TAB: DataStructureTabId = "gl";
const DEFAULT_VIEW_FILTER: ViewFilter = "active";
const DEFAULT_PACKAGE_TAB: PackageTab = "hierarchy";

type PathState =
  | { kind: "list" }
  | { kind: "new-data-package" }
  | { kind: "data-package"; dataPackageId: number; packageTab: PackageTab }
  | { kind: "edit-data-package"; dataPackageId: number }
  | { kind: "new-formula"; dataPackageId: number }
  | { kind: "edit-formula"; dataPackageId: number; formulaId: number }
  | { kind: "new-query"; dataPackageId: number }
  | { kind: "query-detail"; dataPackageId: number; queryId: number }
  | { kind: "edit-query"; dataPackageId: number; queryId: number };

const NEW_QUERY_NAME_PARAM = "name";
const NEW_QUERY_TEMPLATE_TYPE_PARAM = "templateType";

function parseNumericParams<T extends Record<string, string>>(
  pathname: string,
  pattern: string,
): { [K in keyof T]: number } | null {
  const match = matchPath<T>(pathname, { path: pattern, exact: true });
  if (!match) return null;
  const result = {} as { [K in keyof T]: number };
  for (const key of Object.keys(match.params) as (keyof T)[]) {
    const value = Number(match.params[key]);
    if (Number.isNaN(value)) return null;
    result[key] = value;
  }
  return result;
}

function parsePathState(pathname: string, basePath: string): PathState {
  const editQuery = parseNumericParams<{ dataPackageId: string; queryId: string }>(
    pathname,
    `${basePath}/:dataPackageId/queries/:queryId/edit`,
  );
  if (editQuery) {
    return { kind: "edit-query", ...editQuery };
  }

  const newQuery = parseNumericParams<{ dataPackageId: string }>(
    pathname,
    `${basePath}/:dataPackageId/queries/new`,
  );
  if (newQuery) {
    return { kind: "new-query", ...newQuery };
  }

  const queryDetail = parseNumericParams<{ dataPackageId: string; queryId: string }>(
    pathname,
    `${basePath}/:dataPackageId/queries/:queryId`,
  );
  if (queryDetail) {
    return { kind: "query-detail", ...queryDetail };
  }

  const editFormula = parseNumericParams<{ dataPackageId: string; formulaId: string }>(
    pathname,
    `${basePath}/:dataPackageId/formulas/:formulaId/edit`,
  );
  if (editFormula) {
    return { kind: "edit-formula", ...editFormula };
  }

  const newFormula = parseNumericParams<{ dataPackageId: string }>(
    pathname,
    `${basePath}/:dataPackageId/formulas/new`,
  );
  if (newFormula) {
    return { kind: "new-formula", ...newFormula };
  }

  const editDataPackage = parseNumericParams<{ dataPackageId: string }>(
    pathname,
    `${basePath}/:dataPackageId/edit`,
  );
  if (editDataPackage) {
    return { kind: "edit-data-package", ...editDataPackage };
  }

  const packageTabMatch = matchPath<{
    dataPackageId: string;
    packageTab: string;
  }>(pathname, {
    path: `${basePath}/:dataPackageId/:packageTab`,
    exact: true,
  });
  if (packageTabMatch && isValidPackageTab(packageTabMatch.params.packageTab)) {
    const dataPackageId = Number(packageTabMatch.params.dataPackageId);
    if (!Number.isNaN(dataPackageId)) {
      return {
        kind: "data-package",
        dataPackageId,
        packageTab: packageTabMatch.params.packageTab,
      };
    }
    return { kind: "list" };
  }

  const newDataPackageMatch = matchPath(pathname, {
    path: `${basePath}/new`,
    exact: true,
  });
  if (newDataPackageMatch) {
    return { kind: "new-data-package" };
  }

  const dataPackageMatch = matchPath<{ dataPackageId: string }>(pathname, {
    path: `${basePath}/:dataPackageId`,
    exact: true,
  });
  if (dataPackageMatch) {
    const id = Number(dataPackageMatch.params.dataPackageId);
    if (!Number.isNaN(id)) {
      return {
        kind: "data-package",
        dataPackageId: id,
        packageTab: DEFAULT_PACKAGE_TAB,
      };
    }
  }

  return { kind: "list" };
}

function buildSearchString(params: URLSearchParams): string {
  const str = params.toString();
  return str ? `?${str}` : "";
}

export const useDataStructureRouting = ({
  dataStructureBasePath,
  parentPath,
  validTabs,
  defaultTab = DEFAULT_TAB,
  defaultViewFilter = DEFAULT_VIEW_FILTER,
}: UseDataStructureRoutingOptions) => {
  const history = useHistory();
  const location = useLocation();

  const isDataStructureActive = !!matchPath(location.pathname, {
    path: dataStructureBasePath,
  });

  const dataStructureType = useMemo(
    () => parseDataStructureType(location.search, validTabs, defaultTab),
    [location.search, validTabs, defaultTab],
  );

  const viewFilter = useMemo(
    () => parseViewFilter(location.search, defaultViewFilter),
    [location.search, defaultViewFilter],
  );

  const formulaViewFilter = useMemo(
    () => parseViewFilter(location.search, "active", "formulaView"),
    [location.search],
  );

  const queryViewFilter = useMemo(
    () => parseViewFilter(location.search, "active", "queryView"),
    [location.search],
  );

  const pathState = useMemo(
    () => parsePathState(location.pathname, dataStructureBasePath),
    [location.pathname, dataStructureBasePath],
  );

  const dataPackageId =
    pathState.kind === "data-package" ||
    pathState.kind === "edit-data-package" ||
    pathState.kind === "new-formula" ||
    pathState.kind === "edit-formula" ||
    pathState.kind === "new-query" ||
    pathState.kind === "query-detail" ||
    pathState.kind === "edit-query"
      ? pathState.dataPackageId
      : null;

  const packageTab =
    pathState.kind === "data-package" ? pathState.packageTab : DEFAULT_PACKAGE_TAB;

  const formulaId = pathState.kind === "edit-formula" ? pathState.formulaId : null;

  const queryId =
    pathState.kind === "query-detail" || pathState.kind === "edit-query"
      ? pathState.queryId
      : null;

  const isNewDataPackage = pathState.kind === "new-data-package";
  const isEditDataPackage = pathState.kind === "edit-data-package";
  const isNewFormula = pathState.kind === "new-formula";
  const isEditFormula = pathState.kind === "edit-formula";
  const isNewQuery = pathState.kind === "new-query";
  // `isQueryDetail` gates the takeover; `isQueryEdit` flips it editable.
  // New-query is always editable (it's unsaved).
  const isQueryDetail =
    pathState.kind === "query-detail" ||
    pathState.kind === "edit-query" ||
    pathState.kind === "new-query";
  const isQueryEdit = pathState.kind === "edit-query" || pathState.kind === "new-query";

  // The create flow opens `/queries/new?name=<encoded>&templateType=<type>` in
  // a new tab without hitting the BE; the takeover's Save button defers
  // `finpubQueryCreate`. The URL params carry the name and template type across
  // the hop.
  const newQueryName = useMemo(() => {
    if (!isNewQuery) return null;
    const params = new URLSearchParams(location.search);
    const raw = params.get(NEW_QUERY_NAME_PARAM);
    return raw !== null ? raw.trim() : null;
  }, [isNewQuery, location.search]);

  const newQueryTemplateType = useMemo((): SupportedTemplateType | null => {
    if (!isNewQuery) return null;
    const params = new URLSearchParams(location.search);
    const rawTemplateTypeParam = params.get(NEW_QUERY_TEMPLATE_TYPE_PARAM);
    if (
      rawTemplateTypeParam === "ACCOUNT_STATEMENT" ||
      rawTemplateTypeParam === "CAPITAL_CALL_NOTICE" ||
      rawTemplateTypeParam === "DISTRIBUTION_NOTICE"
    ) {
      return rawTemplateTypeParam;
    }
    return null;
  }, [isNewQuery, location.search]);

  const currentQueryParams = useMemo(() => {
    const params = new URLSearchParams();
    params.set("type", dataStructureType);
    if (viewFilter !== defaultViewFilter) {
      params.set("view", viewFilter);
    }
    return params;
  }, [dataStructureType, viewFilter, defaultViewFilter]);

  const navigateToList = useCallback(() => {
    history.push({
      pathname: dataStructureBasePath,
      search: buildSearchString(currentQueryParams),
    });
  }, [history, dataStructureBasePath, currentQueryParams]);

  const navigateToDataStructures = useCallback(() => {
    history.push(dataStructureBasePath);
  }, [history, dataStructureBasePath]);

  const navigateToParent = useCallback(() => {
    history.push(parentPath);
  }, [history, parentPath]);

  const navigateToNewDataPackage = useCallback(() => {
    history.push(`${dataStructureBasePath}/new`);
  }, [history, dataStructureBasePath]);

  const navigateToDataPackage = useCallback(
    (id: number, tab: PackageTab = DEFAULT_PACKAGE_TAB) => {
      history.push(`${dataStructureBasePath}/${id}/${tab}`);
    },
    [history, dataStructureBasePath],
  );

  const navigateToEditDataPackage = useCallback(
    (dpId: number) => {
      history.push(`${dataStructureBasePath}/${dpId}/edit`);
    },
    [history, dataStructureBasePath],
  );

  const navigateToNewFormula = useCallback(
    (dpId: number) => {
      history.push(`${dataStructureBasePath}/${dpId}/formulas/new`);
    },
    [history, dataStructureBasePath],
  );

  const navigateToEditFormula = useCallback(
    (dpId: number, fId: number) => {
      history.push(`${dataStructureBasePath}/${dpId}/formulas/${fId}/edit`);
    },
    [history, dataStructureBasePath],
  );

  const navigateToEditQuery = useCallback(
    (dpId: number, qId: number) => {
      history.push(`${dataStructureBasePath}/${dpId}/queries/${qId}/edit`);
    },
    [history, dataStructureBasePath],
  );

  const navigateToQueryDetail = useCallback(
    (dpId: number, qId: number) => {
      history.push(`${dataStructureBasePath}/${dpId}/queries/${qId}`);
    },
    [history, dataStructureBasePath],
  );

  const navigateToNewQuery = useCallback(
    (dpId: number, name: string, supportedTemplateType?: SupportedTemplateType) => {
      const params = new URLSearchParams({ [NEW_QUERY_NAME_PARAM]: name });
      if (supportedTemplateType) {
        params.set(NEW_QUERY_TEMPLATE_TYPE_PARAM, supportedTemplateType);
      }
      history.push({
        pathname: `${dataStructureBasePath}/${dpId}/queries/new`,
        search: buildSearchString(params),
      });
    },
    [history, dataStructureBasePath],
  );

  const navigateToQueriesTab = useCallback(
    (dpId: number) => {
      history.push(`${dataStructureBasePath}/${dpId}/queries`);
    },
    [history, dataStructureBasePath],
  );

  // Builds an absolute (canopy-prefixed) URL for the Query Detail Takeover.
  // Used when opening the takeover in a new browser tab so the slideout
  // workflow isn't disrupted. Centralised here so the route shape lives in
  // a single place alongside the route matchers above.
  const buildQueryDetailUrl = useCallback(
    (dpId: number, qId: number, mode: "view" | "edit" = "view") => {
      const base = `/canopy${dataStructureBasePath}/${dpId}/queries/${qId}`;
      return mode === "edit" ? `${base}/edit` : base;
    },
    [dataStructureBasePath],
  );

  // Absolute URL for the unsaved takeover. `newQueryName` (above) reads
  // the param back as the initial header/payload.
  const buildNewQueryUrl = useCallback(
    (dpId: number, name: string, supportedTemplateType?: SupportedTemplateType) => {
      const params = new URLSearchParams({ [NEW_QUERY_NAME_PARAM]: name });
      if (supportedTemplateType) {
        params.set(NEW_QUERY_TEMPLATE_TYPE_PARAM, supportedTemplateType);
      }
      return `/canopy${dataStructureBasePath}/${dpId}/queries/new?${params.toString()}`;
    },
    [dataStructureBasePath],
  );

  // After a successful Save, drop the user on the read-only view route —
  // works for both "/new" (post-create) and "/:id/edit" (post-update).
  // `replace` (not `push`) so Back doesn't land on the stale URL.
  const replaceToViewQuery = useCallback(
    (dpId: number, qId: number) => {
      history.replace(`${dataStructureBasePath}/${dpId}/queries/${qId}`);
    },
    [history, dataStructureBasePath],
  );

  const navigateToPackageTab = useCallback(
    (dpId: number, tab: PackageTab) => {
      history.push(`${dataStructureBasePath}/${dpId}/${tab}`);
    },
    [history, dataStructureBasePath],
  );

  const handleTypeChange = useCallback(
    (type: DataStructureTabId) => {
      const params = new URLSearchParams(location.search);
      params.set("type", type);
      history.push({
        pathname: dataStructureBasePath,
        search: buildSearchString(params),
      });
    },
    [history, location.search, dataStructureBasePath],
  );

  const handleViewFilterChange = useCallback(
    (filter: ViewFilter) => {
      const params = new URLSearchParams(location.search);
      if (filter === defaultViewFilter) {
        params.delete("view");
      } else {
        params.set("view", filter);
      }
      history.push({
        pathname: location.pathname,
        search: buildSearchString(params),
      });
    },
    [history, location.search, location.pathname, defaultViewFilter],
  );

  const handleFormulaViewFilterChange = useCallback(
    (filter: ViewFilter) => {
      const params = new URLSearchParams(location.search);
      if (filter === "active") {
        params.delete("formulaView");
      } else {
        params.set("formulaView", filter);
      }
      history.push({
        pathname: location.pathname,
        search: buildSearchString(params),
      });
    },
    [history, location.search, location.pathname],
  );

  const handleQueryViewFilterChange = useCallback(
    (filter: ViewFilter) => {
      const params = new URLSearchParams(location.search);
      if (filter === "active") {
        params.delete("queryView");
      } else {
        params.set("queryView", filter);
      }
      history.push({
        pathname: location.pathname,
        search: buildSearchString(params),
      });
    },
    [history, location.search, location.pathname],
  );

  return {
    isDataStructureActive,
    dataStructureType,
    viewFilter,
    pathState,
    dataPackageId,
    packageTab,
    formulaId,
    queryId,
    isNewDataPackage,
    isEditDataPackage,
    isNewFormula,
    isEditFormula,
    isNewQuery,
    newQueryName,
    newQueryTemplateType,
    isQueryDetail,
    isQueryEdit,
    navigateToList,
    navigateToDataStructures,
    navigateToParent,
    navigateToNewDataPackage,
    navigateToDataPackage,
    navigateToEditDataPackage,
    navigateToNewFormula,
    navigateToEditFormula,
    navigateToEditQuery,
    navigateToQueryDetail,
    navigateToNewQuery,
    navigateToQueriesTab,
    navigateToPackageTab,
    buildQueryDetailUrl,
    buildNewQueryUrl,
    replaceToViewQuery,
    handleTypeChange,
    handleViewFilterChange,
    formulaViewFilter,
    handleFormulaViewFilterChange,
    queryViewFilter,
    handleQueryViewFilterChange,
  };
};
